import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import constants from './constants';
import noop from '../utils/noop';
import Tooltip from '../tooltip';

const { DRAGGABLE_BORDER_WIDTH } = constants;

const TOOLTIP_ID = 'draggable-coords';

class DraggableObserver extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isDragged: false,
      startX: 0,
      startY: 0,
    };
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);

    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.stopDragging = this.stopDragging.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.imageDragZone = React.createRef();
  }

  componentDidMount() {
    document.body.addEventListener('mouseleave', this.handleMouseLeave);
  }

  componentWillUnmount() {
    document.body.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  stopDragging(event) {
    const { onAfterChange } = this.props;
    const { isDragged } = this.state;
    this.setState({
      isDragged: false,
    });
    onAfterChange(event, isDragged);
  }

  handleTouchStart(event) {
    const [touch] = event.touches;
    const { clientX, clientY } = touch;

    const { onBeforeChange } = this.props;

    this.setState({
      isDragged: true,
      startX: clientX,
      startY: clientY,
    });

    onBeforeChange({ startX: clientX, startY: clientY }, event);
  }

  handleTouchMove(event) {
    event.stopPropagation();
    const { onChange } = this.props;
    const { startY, startX } = this.state;
    const [touch] = event.touches;
    const { clientX, clientY } = touch;
    const x = clientX - startX;
    const y = clientY - startY;

    onChange({ x, y }, event);
  }

  handleTouchEnd(event) {
    const { onAfterChange } = this.props;
    const { isDragged } = this.state;
    this.setState({
      isDragged: false,
    });

    onAfterChange(event, isDragged);
  }

  handleMouseDown(event) {
    const { onBeforeChange } = this.props;
    this.setState({
      isDragged: true,
      startX: event.clientX,
      startY: event.clientY,
    });
    onBeforeChange({ x: event.clientX, y: event.clientY }, event);
  }

  handleMouseMove(event) {
    event.stopPropagation();
    const { onChange } = this.props;
    const { startY, startX } = this.state;

    const x = event.clientX - startX;
    const y = event.clientY - startY;
    onChange({ x, y }, event);
  }

  handleMouseUp(event) {
    const { onAfterChange } = this.props;
    const { isDragged } = this.state;
    this.setState({
      isDragged: false,
    });

    onAfterChange(event, isDragged);
  }

  handleMouseLeave(event) {
    this.stopDragging(event);
  }

  handleOverlayClick(event) {
    const { onOverlayClick } = this.props;
    if (event.target !== this.imageDragZone.current) {
      onOverlayClick();
    }
  }

  render() {
    const { targetElementBounds, borderWidth, borderRadius, onDoubleClick, tooltipText } =
      this.props;
    const { borderRadiusAxisX, borderRadiusAxisY } = borderRadius;
    const { isDragged } = this.state;
    if (!targetElementBounds) return null;
    const { width, height, top, bottom, right, left } = targetElementBounds;

    const imageHighlighterStyles = {
      width: width - borderWidth * 2,
      height: height - borderWidth * 2,
      top,
      bottom,
      right,
      left,
      border: `${borderWidth}px solid #FFF`,
      borderRadius: `${borderRadiusAxisX} / ${borderRadiusAxisY}`,
    };
    return (
      <div
        className="ws-drag-observer"
        onMouseMove={isDragged ? this.handleMouseMove : null}
        onMouseUp={this.handleMouseUp}
        onTouchMove={isDragged ? this.handleTouchMove : null}
        onDoubleClick={onDoubleClick}
        onClick={this.handleOverlayClick}
      >
        <div
          ref={this.imageDragZone}
          style={imageHighlighterStyles}
          className="ws-image-highlighter"
          onMouseDown={this.handleMouseDown}
          onTouchStart={this.handleTouchStart}
          onTouchEnd={this.handleTouchEnd}
          {...(tooltipText && {
            'data-tip': '',
            'data-for': TOOLTIP_ID,
          })}
        />
        {tooltipText && (
          <div
            className={classNames('ws-drag-observer__tooltip-container', {
              'ws-drag-observer__tooltip-container--visible': isDragged,
            })}
          >
            <Tooltip id={TOOLTIP_ID} effect="float" place="bottom">
              {tooltipText}
            </Tooltip>
          </div>
        )}
      </div>
    );
  }
}

DraggableObserver.defaultProps = {
  borderWidth: DRAGGABLE_BORDER_WIDTH,
  onChange: noop,
  onBeforeChange: noop,
  onAfterChange: noop,
  onDoubleClick: noop,
  onOverlayClick: noop,
  tooltipText: null,
};

DraggableObserver.propTypes = {
  targetElementBounds: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired,
    bottom: PropTypes.number.isRequired,
    right: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
  }).isRequired,
  borderRadius: PropTypes.shape({
    borderRadiusAxisX: PropTypes.string,
    borderRadiusAxisY: PropTypes.string,
  }).isRequired,
  borderWidth: PropTypes.number,
  onChange: PropTypes.func,
  onBeforeChange: PropTypes.func,
  onAfterChange: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onOverlayClick: PropTypes.func,
  tooltipText: PropTypes.string,
};

export default DraggableObserver;
